import React, { useMemo, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import PngDownloadButton from './PngDownloadButton';
import { downloadSvgAsPng } from '../../../utils/svgExport';

// Task 15 — Devir & Kohort sayfasının Kaplan-Meier hayatta kalma (elde tutma) eğrisi.
//
// STATİSTİKSEL DÜRÜSTLÜK GEREĞİ (brief, spec §9.2): eğrinin altındaki RİSK ALTINDAKİ SAYI
// TABLOSU zorunludur. Bir hayatta kalma eğrisinin sağ ucu neredeyse her zaman çok az kişiye
// dayanır (solda yüzlerce kişiyle hesaplanan bir nokta ile sağda 2-3 kişiyle hesaplanan bir
// nokta AYNI görsel güvenle çizilir) — tablo olmadan izleyici sağ ucu solla aynı güvenilirlikte
// okur. Bu yüzden risk-tablosu burada AYRI bir bileşen/adım değil, eğriyle aynı SVG içinde, AYNI
// x() ölçek fonksiyonuyla hizalanmış olarak üretilir (bkz. aşağıdaki `plottable.map` — hem
// eğrinin basamak noktası hem tablo sütunu AYNI `x(p.tenureMonth)` çağrısından gelir, iki ayrı
// hesap YOKTUR; "tabloyu eğriye hizalama" sorusu bu yüzden yapısal olarak çözülür, ayrıca
// senkronize edilmesi gereken bir CSS/DOM hizalaması değildir).
//
// BASAMAK (STEP), DÜZ ÇİZGİ DEĞİL: Kaplan-Meier tahmini olaylar ARASINDA sabittir, yalnız her
// olay anında DÜŞER (bkz. KaplanMeierEstimator.Estimate — S(t) = önceki S(t) × (1 - olay/risk)).
// `buildStepPath` bu yüzden yalnızca SVG M (taşı) / H (yatay) / V (dikey) komutları üretir; bu
// üçü DIŞINDA bir komut YAZILMAZ, dolayısıyla yol GEOMETRİK OLARAK diyagonal/eğrisel olamaz
// (SurvivalCurve.test.js bunu `d` attribute'unda M/H/V dışında hiçbir komut olmadığını
// doğrulayarak pinler).
//
// VERİ BİTİNCE UZATMA: x ekseninin üst sınırı SON PLOT EDİLEN noktanın kıdem ayıdır (brief:
// "backend zaten veri ötesinde nokta üretmiyor, senin işin de PADLEMEMEK"). `buildStepPath`
// döngüsü son noktada durur — ekstra bir "ileri" H/V segmenti YAZILMAZ.
//
// BASTIRMA: HaSurvivalPointResponse'ta bir nokta bastırılmışsa Value VE N BİRLİKTE null gelir
// (N sızdırılmaz — bkz. o DTO'nun yorumu). `isPlottablePoint` FunnelPlot.isPlottable ile AYNI
// strict null/undefined kontrolünü kullanır (value=0 tam sönme, n gibi anlamlı ama falsy
// değerleri YUTMAZ) — bastırılmış nokta hem eğriden hem risk tablosundan sessizce DIŞLANIR,
// 0 ya da başka bir uydurma değerle yer tutucu olarak da EKLENMEZ.
//
// Y EKSENİ SABİT [0,1]: FunnelPlot'un dinamik y-domain'inin AKSİNE burada eksen HER ZAMAN
// %0-%100 aralığındadır. Hayatta kalma olasılığı doğası gereği [0,1] ile sınırlıdır; ekseni
// daraltmak (ör. %70-%100) küçük bir düşüşü olduğundan büyük göstererek TAM OLARAK bu modülün
// karşı olduğu yanıltmayı üretirdi — bilinçli bir farklılaşma, FunnelPlot'un desenini kör kör
// kopyalamak değil.
//
// TEK SERİ, LEJANT YOK: dataviz kuralı ("bir seri lejant kutusu gerektirmez, başlık zaten neyin
// çizildiğini söyler") — FunnelPlot'un üç-durumlu (normal/uyarı/alarm) lejantının aksine burada
// tek bir eğri var, ayrı bir renk sınıflandırması YOK.
const VIEW_WIDTH = 640;
const DEFAULT_HEIGHT = 320;
const MARGIN = { top: 24, right: 28, bottom: 64, left: 56 };

const POINT_RADIUS = 5; // dataviz: işaretleyici ≥8px çap (r≥4) — FunnelPlot'un "normal" yarıçapıyla aynı.
const HIT_AREA_RADIUS = 12; // dataviz: nokta isabet alanı ≥24px çap.

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Caption = styled.p`
  margin: 0;
  padding: 0 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ToolbarRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// Bastırılmış bir nokta Value VE N'yi BİRLİKTE null taşır (HaSurvivalPointResponse yorumu) —
// FunnelPlot.isPlottable ile AYNI felsefe: falsy kontrolü DEĞİL, yalnızca null/undefined
// kontrolü (value=0 tam sönme anlamına gelebilir, n'nin kendisi de 0 OLAMAZ ama savunmacı
// olarak falsy tuzağına düşülmez).
function isPlottablePoint(p) {
  return (
    !!p &&
    p.value !== null && p.value !== undefined &&
    p.n !== null && p.n !== undefined
  );
}

function linearScale(domainMax, rangeMin, rangeMax) {
  const safeMax = domainMax > 0 ? domainMax : 1;
  return (v) => rangeMin + (Math.max(0, Math.min(v, safeMax)) / safeMax) * (rangeMax - rangeMin);
}

// Kaplan-Meier basamak (step) yolu — dosya başı yorumuna bkz. S(0)=1 varsayımı bir
// EKSTRAPOLASYON değil, tanımın kendisidir (zaman 0'da kimse henüz ayrılmamıştır); eğri bu
// yüzden (0, %100)'den başlar ve ilk olay anına kadar YATAY kalır. Son noktadan SONRA hiçbir
// segment üretilmez.
function buildStepPath(points, xFn, yFn) {
  if (points.length === 0) return null;
  // Komut harfi SAYIYA BİTİŞİK yazılır (ör. "H208.5", "M56,24") — SVG için geçerli (harf
  // kendisi zaten belirsizliği kaldırır) VE her komutu TEK bir boşluk-ayrılmış token yapar;
  // bu, testin `d.split(/\s+/)` ile her token'ın komut harfiyle BAŞLADIĞINI doğrulamasını
  // basitleştirir (bkz. SurvivalCurve.test.js).
  let d = `M${xFn(0)},${yFn(1)}`;
  points.forEach((p) => {
    d += ` H${xFn(p.tenureMonth)} V${yFn(p.value)}`;
  });
  return d;
}

const Y_TICKS = [0, 0.25, 0.5, 0.75, 1];

export default function SurvivalCurve({ points, height = DEFAULT_HEIGHT }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const svgRef = useRef(null);

  const percentFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 }),
    [i18n.language]
  );
  const countFormatter = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  // Sıra: backend ZATEN tenureMonth'a göre artan sırada döner (brief) — burada YENİDEN
  // sıralanmaz (decode/reorder etme kuralı, brief). Yalnızca bastırılmış/eksik noktalar
  // filtrelenir.
  const plottable = useMemo(() => (points || []).filter(isPlottablePoint), [points]);

  if (plottable.length === 0) {
    return <EmptyState>{t('hrAnalytics.survivalCurve.empty')}</EmptyState>;
  }

  const innerWidth = VIEW_WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  // Global Kısıt / brief: "veri bitince eğriyi UZATMA" — x ekseninin üst sınırı SON plot
  // edilen (görünür) noktanın kıdem ayıdır, daha fazlası DEĞİL.
  const maxTenureMonth = plottable[plottable.length - 1].tenureMonth;

  const xScale = linearScale(maxTenureMonth, 0, innerWidth);
  const x = (v) => MARGIN.left + xScale(v);
  const yScale = linearScale(1, 0, innerHeight); // sabit [0,1] — dosya başı yorumu.
  const y = (v) => MARGIN.top + innerHeight - yScale(v);

  const axisY = MARGIN.top + innerHeight;
  const stepPath = buildStepPath(plottable, x, y);

  // Etiket seyreltme (kullanıcı geri bildirimi 2026-08-03: "x ekseni okunmuyor, yazılar
  // birbirine girmiş"): 640px genişliğe ~12 sütundan fazlası sığmıyor (3 haneli kalın N
  // değerleri çakışıyor). EĞRİNİN KENDİSİ SEYRELTİLMEZ — her nokta çizilmeye ve tooltip
  // taşımaya devam eder; yalnız eksen etiketi + risk-tablosu sütunu her labelStep'inci
  // noktada basılır. Son nokta, bir önceki etikete yeterince uzaksa AYRICA etiketlenir
  // (eğrinin nerede bittiği okunabilsin); çok yakınsa atlanır ki son iki etiket çakışmasın.
  // Küçük veri setlerinde (nokta sayısı <= 12) labelStep=1'dir — davranış birebir eskisi.
  const MAX_LABEL_COLUMNS = 12;
  const labelStep = Math.ceil(plottable.length / MAX_LABEL_COLUMNS);
  const lastIndex = plottable.length - 1;
  const isLabelled = (i) =>
    i % labelStep === 0 ||
    (i === lastIndex && lastIndex % labelStep >= Math.ceil(labelStep / 2));

  const handleDownload = () => {
    downloadSvgAsPng(svgRef.current, {
      fileName: 'elde-tutma-egrisi.png',
      background: theme.colors.surface.card,
    }).catch(() => {}); // indirme başarısızlığı sessizce yutulur — API hatası değil, toast kuralı dışı
  };

  return (
    <Wrapper>
      <ToolbarRow>
        <PngDownloadButton dataRole="survival-download-png" onClick={handleDownload} />
      </ToolbarRow>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={t('hrAnalytics.survivalCurve.chartAriaLabel')}
      >
        {/* y ızgara çizgileri + % etiketleri — dataviz: hairline (1px), düz, tema token'ı */}
        {Y_TICKS.map((tv) => (
          <g key={`yt-${tv}`}>
            <line
              x1={MARGIN.left} y1={y(tv)}
              x2={MARGIN.left + innerWidth} y2={y(tv)}
              stroke={theme.colors.surface.border} strokeWidth={1}
            />
            <text
              x={MARGIN.left - 8} y={y(tv) + 3}
              textAnchor="end" fontSize={10}
              fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {percentFormatter.format(tv)}
            </text>
          </g>
        ))}

        {/* x ekseni çizgisi */}
        <line
          x1={MARGIN.left} y1={axisY} x2={MARGIN.left + innerWidth} y2={axisY}
          stroke={theme.colors.surface.border} strokeWidth={1}
        />

        {/* risk tablosunu üstteki eksenden ayıran ince çizgi (gerçek bir "tablo" okuması) */}
        <line
          x1={MARGIN.left} y1={axisY + 22} x2={MARGIN.left + innerWidth} y2={axisY + 22}
          stroke={theme.colors.surface.border} strokeWidth={1}
        />

        {/* Kaplan-Meier basamak eğrisi — tek seri, lejant YOK (dataviz: "bir seri lejant
            kutusu gerektirmez"). Renk tek başına anlam TAŞIMIYOR (yalnız bir eğri var);
            geometri (basamak şekli) zaten kendi başına bir görsel kodlama. */}
        <path data-role="survival-path" d={stepPath} fill="none" stroke={theme.colors.primary.main} strokeWidth={2} strokeLinejoin="round" />

        {plottable.map((p, i) => {
          const px = x(p.tenureMonth);
          const py = y(p.value);
          const labelled = isLabelled(i);
          const tooltipText = [
            `${t('hrAnalytics.survivalCurve.tenureMonthLabel')} ${countFormatter.format(p.tenureMonth)}`,
            `${percentFormatter.format(p.value)}`,
            `(${t('hrAnalytics.survivalCurve.atRiskLabel')}: ${countFormatter.format(p.n)})`,
          ].join(' — ');

          return (
            <g key={p.tenureMonth}>
              {/* basamak noktası işaretleyici — brief: "tooltip GATE etmez", değer zaten
                  aşağıdaki risk-tablosu sütununda GÖRÜNÜR metin olarak var; bu yalnız bir
                  zenginleştirme. */}
              <g
                data-role="survival-point"
                data-tenure-month={p.tenureMonth}
                tabIndex={0}
                role="img"
                aria-label={tooltipText}
              >
                <title>{tooltipText}</title>
                <circle cx={px} cy={py} r={HIT_AREA_RADIUS} fill="transparent" />
                <circle cx={px} cy={py} r={POINT_RADIUS} fill={theme.colors.primary.main} stroke={theme.colors.surface.card} strokeWidth={2} />
              </g>

              {/* kene işareti + kıdem ayı etiketi (AYNI ZAMANDA x ekseninin tek tik sistemi —
                  ayrı, yuvarlak sayılı bir tik seti İCAT EDİLMEDİ: brief risk tablosunun her
                  plot edilen nokta için bir sütun istiyordu, ama 12'den çok noktada yazılar
                  fiziksel olarak çakışıyor — etiketler labelStep ile seyreltilir (bkz. yukarı),
                  eğri noktaları ve tooltip'leri TAM kalır). */}
              {labelled && (
                <>
                  <line x1={px} y1={axisY} x2={px} y2={axisY + 4} stroke={theme.colors.surface.border} strokeWidth={1} />
                  <text
                    x={px} y={axisY + 16}
                    textAnchor="middle" fontSize={10}
                    fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {countFormatter.format(p.tenureMonth)}
                  </text>

                  {/* risk tablosu sütunu — data-role/data-tenure-month/data-x/data-n test
                      kancası. data-x, eğrinin KENDİ x(p.tenureMonth) çağrısıyla AYNI değeri
                      taşır (bkz. dosya başı yorumu) — hizalama iki ayrı hesaptan değil, TEK
                      bir paylaşılan `x` kapanışından gelir. */}
                  <text
                    data-role="at-risk-column"
                    data-tenure-month={p.tenureMonth}
                    data-x={px}
                    data-n={p.n}
                    x={px} y={axisY + 34}
                    textAnchor="middle" fontSize={10} fontWeight={700}
                    fontFamily={theme.metrics.font.family} fill={theme.colors.text.primary}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {countFormatter.format(p.n)}
                  </text>
                </>
              )}
            </g>
          );
        })}

        {/* eksen başlıkları */}
        <text
          x={MARGIN.left + innerWidth / 2} y={height - 6}
          textAnchor="middle" fontSize={11}
          fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
        >
          {t('hrAnalytics.survivalCurve.xAxisLabel')}
        </text>
        <text
          x={14} y={MARGIN.top + innerHeight / 2}
          textAnchor="middle" fontSize={11}
          fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
          transform={`rotate(-90 14 ${MARGIN.top + innerHeight / 2})`}
        >
          {t('hrAnalytics.survivalCurve.yAxisLabel')}
        </text>
      </svg>

      {/* dataviz: "her chart'ın bir tablo-görünümü ikizi vardır" — risk tablosu zaten SVG
          içinde GÖRÜNÜR metin olarak var (yukarıda); bu satır onu nasıl okuyacağını anlatan
          erişilebilir metin (brief'in istatistiksel dürüstlük gerekçesinin kullanıcıya
          aktarımı). */}
      <Caption>{t('hrAnalytics.survivalCurve.atRiskCaption')}</Caption>
    </Wrapper>
  );
}
